import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, Instagram, MapPin, Clock, ShoppingBag, Menu, X } from "lucide-react";
import { LOGO } from "../lib/constants";
import { api } from "../lib/apiClient";
import { buildWhatsappUrl } from "../lib/whatsapp";
import { PublicBottomNav } from "./PublicBottomNav";
import { useCart } from "../hooks/useCart";

export const PublicLayout = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { count } = useCart();

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const wa = buildWhatsappUrl(settings.whatsapp, "Hola Kelifa 🌸, me gustaría más información.");

  return (
    <div className="min-h-screen flex flex-col bg-crema">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-blancoCalido/90 backdrop-blur-md border-b border-beige">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
            <img src={LOGO} alt="Kelifa" className="w-10 h-10 rounded-full object-cover shadow-pastel" />
            <span className="brand-title-brasika text-2xl sm:text-3xl text-vino leading-none">Kelifa<span className="hidden sm:inline"> Reposteria</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-cafe/80 text-sm font-medium">
            <Link to="/catalogo" data-testid="nav-catalogo" className="hover:text-vino transition-colors">Catálogo</Link>
            <Link to="/servicios" data-testid="nav-servicios" className="hover:text-vino transition-colors">Servicios</Link>
            <a href={wa} target="_blank" rel="noreferrer" className="hover:text-vino transition-colors">Contacto</a>
            <button data-testid="nav-cart-btn" onClick={() => navigate("/carrito")}
              className="relative hover:text-vino transition-colors flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /> Carrito
              {count > 0 && <span className="absolute -top-3 -right-4 bg-vino text-crema text-[10px] font-bold min-w-5 h-5 px-1 rounded-full flex items-center justify-center">{count}</span>}
            </button>
            <button data-testid="nav-pedido-btn" onClick={() => navigate("/pedido")}
              className="bg-vino text-crema rounded-full px-5 py-2.5 hover:bg-vinoDark transition-all hover:-translate-y-0.5 shadow-md flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /> Hacer un pedido
            </button>
          </nav>
          <div className="md:hidden flex items-center gap-3">
            <button onClick={() => navigate("/carrito")} className="relative text-vino" aria-label="Ver carrito">
              <ShoppingBag className="w-6 h-6" />
              {count > 0 && <span className="absolute -top-2 -right-2 bg-vino text-crema text-[10px] font-bold min-w-5 h-5 px-1 rounded-full flex items-center justify-center">{count}</span>}
            </button>
            <button className="text-vino" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-beige bg-blancoCalido px-4 py-4 flex flex-col gap-3">
            <Link to="/catalogo" onClick={() => setOpen(false)} className="py-2 text-cafe">Catálogo</Link>
            <Link to="/servicios" onClick={() => setOpen(false)} className="py-2 text-cafe">Servicios</Link>
            <Link to="/carrito" onClick={() => setOpen(false)} className="py-2 text-cafe flex items-center justify-between">
              <span>Carrito</span>
              {count > 0 && <span className="bg-vino text-crema text-[10px] font-bold min-w-5 h-5 px-1 rounded-full flex items-center justify-center">{count}</span>}
            </Link>
            <a href={wa} target="_blank" rel="noreferrer" className="py-2 text-cafe">Contacto</a>
            <button onClick={() => { setOpen(false); navigate("/pedido"); }} className="bg-vino text-crema rounded-full px-5 py-3 mt-1">Hacer un pedido</button>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-vinoDark text-crema/90 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-12 grid gap-8 sm:grid-cols-3">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <img src={LOGO} alt="Kelifa" className="w-10 h-10 rounded-full" />
              <span className="brand-title-brasika text-3xl">Kelifa Reposteria</span>
            </div>
            <p className="text-sm text-crema/70">Un bocado, una sonrisa.</p>
          </div>
          <div className="text-sm space-y-2">
            <p className="font-semibold text-crema mb-2">Contacto</p>
            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-rosa" /> {settings.pickup_address || "Villahermosa, Tabasco"}</p>
            <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-rosa" /> {settings.visible_hours || "Lun a Sáb · 9:00 a 18:00"}</p>
            <a href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white"><MessageCircle className="w-4 h-4 text-rosa" /> WhatsApp</a>
            {settings.instagram && <a href={`https://instagram.com/${settings.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white"><Instagram className="w-4 h-4 text-rosa" /> @{settings.instagram}</a>}
          </div>
          <div className="text-sm space-y-2">
            <p className="font-semibold text-crema mb-2">Explora</p>
            <Link to="/catalogo" className="block hover:text-white">Catálogo</Link>
            <Link to="/servicios" className="block hover:text-white">Servicios para eventos</Link>
            <Link to="/pedido" className="block hover:text-white">Hacer un pedido</Link>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 pb-28 lg:pb-4 text-center text-xs text-crema/50">
          © {new Date().getFullYear()} Kelifa Repostería · Hecho con cariño 🍰
        </div>
      </footer>

      {/* Floating WhatsApp (desktop only; mobile uses bottom nav) */}
      <a href={wa} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
        className="hidden lg:flex fixed bottom-5 right-5 z-50 bg-[#25D366] text-white rounded-full p-4 shadow-lg hover:scale-110 transition-transform">
        <MessageCircle className="w-6 h-6" />
      </a>

      <PublicBottomNav whatsapp={settings.whatsapp} />
    </div>
  );
};
